import { Component } from "react";
import { message, Typography, List, Checkbox, Tag, Collapse, Empty, Tooltip } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import { ModalItem, ButtonItem, Form } from "../../../../Components/Items";
import { BoxPlotOutlined, CheckSquareOutlined } from "@ant-design/icons";

const { Text } = Typography;

// Revisión de picking para "Marcar empaquetado" en lote: una venta por
// panel colapsable, insumos pre-marcados como "listos" pero editables antes
// de confirmar — igual que el checklist individual, para las N ventas
// seleccionadas a la vez.
class ModalConfirmarEmpaquetadoLote extends Component {
  state = {
    loading: false,
    ventasInsumos: [],
    itemsEmpaquetados: {}, // { [ventaId]: { [insumoId]: bool } }
    insumosBloqueados: {},
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible && this.props.ventaIds?.length) {
      this.consultarInsumos();
    }
    if (!this.props.visible && prevProps.visible) {
      this.setState({ ventasInsumos: [], itemsEmpaquetados: {}, insumosBloqueados: {} });
    }
  }

  consultarInsumos = () => {
    const { empresaId, headersToken, ventaIds } = this.props;
    this.setState({ loading: true });
    axios
      .post(
        "ventasEcommerce/obtenerInsumosParaLote",
        { empresaId, ventaIds },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          const ventasInsumos = res.data.data || [];
          const itemsEmpaquetados = {};
          const insumosBloqueados = {};
          ventasInsumos.forEach((v) => {
            itemsEmpaquetados[v.ventaId] = {};
            insumosBloqueados[v.ventaId] = {};
            (v.insumos || []).forEach((ins) => {
              itemsEmpaquetados[v.ventaId][ins._id] = true;
              insumosBloqueados[v.ventaId][ins._id] = !!ins.empaquetado;
            });
          });
          this.setState({ ventasInsumos, itemsEmpaquetados, insumosBloqueados });
        } else {
          message.warning(res.data.message || "No se pudieron obtener los insumos.");
        }
      })
      .catch(() => message.error("Error al obtener los insumos de las ventas seleccionadas."))
      .finally(() => this.setState({ loading: false }));
  };

  toggleInsumo = (ventaId, insumoId) => {
    const { insumosBloqueados, itemsEmpaquetados } = this.state;
    const bloqueado = !!insumosBloqueados[ventaId]?.[insumoId];
    const marcado = !!itemsEmpaquetados[ventaId]?.[insumoId];
    if (bloqueado && marcado && !this.props.permisoDesempaquetar) {
      return message.warning(
        "No tienes permiso para desempaquetar un insumo ya confirmado.",
      );
    }
    this.setState((prev) => ({
      itemsEmpaquetados: {
        ...prev.itemsEmpaquetados,
        [ventaId]: {
          ...prev.itemsEmpaquetados[ventaId],
          [insumoId]: !prev.itemsEmpaquetados[ventaId]?.[insumoId],
        },
      },
    }));
  };

  marcarTodo = () => {
    const { ventasInsumos, itemsEmpaquetados, insumosBloqueados } = this.state;
    const permiso = this.props.permisoDesempaquetar;
    const todosMarcados = ventasInsumos.every((v) =>
      (v.insumos || []).every((ins) => itemsEmpaquetados[v.ventaId]?.[ins._id]),
    );
    const nuevoValor = !todosMarcados;
    const nuevos = {};
    ventasInsumos.forEach((v) => {
      const actuales = { ...itemsEmpaquetados[v.ventaId] };
      (v.insumos || []).forEach((ins) => {
        if (!nuevoValor && insumosBloqueados[v.ventaId]?.[ins._id] && !permiso) return;
        actuales[ins._id] = nuevoValor;
      });
      nuevos[v.ventaId] = actuales;
    });
    this.setState({ itemsEmpaquetados: nuevos });
  };

  marcarPorObligatorio = (valor) => {
    const { ventasInsumos, itemsEmpaquetados, insumosBloqueados } = this.state;
    const permiso = this.props.permisoDesempaquetar;
    const objetivos = ventasInsumos.flatMap((v) =>
      (v.insumos || [])
        .filter((ins) => ins.obligatorio === valor)
        .map((ins) => ({ ventaId: v.ventaId, insumoId: ins._id })),
    );
    if (objetivos.length === 0) return;
    const todosMarcados = objetivos.every(
      ({ ventaId, insumoId }) => itemsEmpaquetados[ventaId]?.[insumoId],
    );
    const nuevoValor = !todosMarcados;
    this.setState((prev) => {
      const nuevos = { ...prev.itemsEmpaquetados };
      objetivos.forEach(({ ventaId, insumoId }) => {
        if (!nuevoValor && insumosBloqueados[ventaId]?.[insumoId] && !permiso) return;
        nuevos[ventaId] = { ...nuevos[ventaId], [insumoId]: nuevoValor };
      });
      return { itemsEmpaquetados: nuevos };
    });
  };

  confirmar = () => {
    const { ventasInsumos, itemsEmpaquetados } = this.state;
    const items = ventasInsumos.map((v) => ({
      ventaId: v.ventaId,
      insumosEmpaquetados: itemsEmpaquetados[v.ventaId] || {},
    }));
    this.props.onConfirmar(items);
  };

  render() {
    const { visible, onCancel, escuelaActivo, permisoDesempaquetar } = this.props;
    const { loading, ventasInsumos, itemsEmpaquetados, insumosBloqueados } = this.state;

    return (
      <ModalItem
        title={
          <span>
            <BoxPlotOutlined /> Marcar empaquetado en lote ({ventasInsumos.length} pedido(s))
          </span>
        }
        visible={visible}
        onCancel={onCancel}
        width={800}
        spinning={loading}
      >
        <Form
          name="form_empaquetado_lote"
          bottom={
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tooltip title="Marcar todo (todas las ventas)">
                <ButtonItem
                  size="small"
                  azul
                  icon={<CheckSquareOutlined />}
                  onClick={this.marcarTodo}
                />
              </Tooltip>
              {escuelaActivo && (
                <>
                  <Tooltip title="Marcar obligatorios">
                    <ButtonItem
                      size="small"
                      morado
                      icon={<CheckSquareOutlined />}
                      onClick={() => this.marcarPorObligatorio(true)}
                    />
                  </Tooltip>
                  <Tooltip title="Marcar no obligatorios">
                    <ButtonItem
                      size="small"
                      naranja
                      icon={<CheckSquareOutlined />}
                      onClick={() => this.marcarPorObligatorio(false)}
                    />
                  </Tooltip>
                </>
              )}
              <ButtonItem rojo onClick={onCancel}>
                Cancelar
              </ButtonItem>
              <ButtonItem
                type="primary"
                verde
                disabled={ventasInsumos.length === 0}
                onClick={this.confirmar}
              >
                Confirmar empaquetado en lote
              </ButtonItem>
            </div>
          }
        >
          {ventasInsumos.length === 0 && !loading ? (
            <Empty description="No se encontraron insumos para las ventas seleccionadas." />
          ) : (
            <Collapse defaultActiveKey={ventasInsumos.map((v) => v.ventaId)}>
              {ventasInsumos.map((v) => (
                <Collapse.Panel
                  key={v.ventaId}
                  header={
                    <span>
                      <Tag color="magenta">{v.serieNumero}</Tag>
                      <Text strong>{v.cliente}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        ({(v.insumos || []).length} insumo(s))
                      </Text>
                    </span>
                  }
                >
                  {(v.insumos || []).length === 0 ? (
                    <Text type="secondary">Sin insumos físicos que empacar.</Text>
                  ) : (
                    <List
                      size="small"
                      dataSource={v.insumos}
                      renderItem={(ins) => (
                        <List.Item
                          actions={[
                            <Checkbox
                              checked={!!itemsEmpaquetados[v.ventaId]?.[ins._id]}
                              disabled={
                                !!insumosBloqueados[v.ventaId]?.[ins._id] &&
                                !!itemsEmpaquetados[v.ventaId]?.[ins._id] &&
                                !permisoDesempaquetar
                              }
                              onChange={() => this.toggleInsumo(v.ventaId, ins._id)}
                            >
                              Listo
                            </Checkbox>,
                          ]}
                        >
                          <Text>{ins.descripcion}</Text>
                          <Tag color="magenta" style={{ marginLeft: 8 }}>
                            Cant: {ins.cantidad}
                          </Tag>
                        </List.Item>
                      )}
                    />
                  )}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </Form>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  escuelaActivo: !!state.empresa.escuela,
  permisoDesempaquetar:
    state.user.tipo === 0 ||
    !!state.user.permisos.find(
      (valuePermiso) => valuePermiso.permisoId.toString() === "6a629819f04f1ced48a83955",
    )?.activado,
});

export default connect(mapStateToProps)(ModalConfirmarEmpaquetadoLote);
